import React, { Suspense, lazy, useEffect } from 'react';
import { LinearProgress } from '@mui/material';
import ErrorMsg from '../../common/ErrorMsg';
import errorResponses from '../../../errorResponses';
import { AUTH_CONFIG } from '../../../auth0-config';
import { AppContext } from '../../../App';
import { IExtraction, IToken } from '../../../types/my-types';
import { IFieldReview } from '../form/FieldList';
import { MarkDispatchContext, MarkStateContext } from '../../../context/MarkContext';
import { useCreateCorrection } from '../../hooks/useCreateCorrection';
import { usePage } from '../../hooks/usePage';
import { useQueryClient } from '@tanstack/react-query';
import { getNextActiveField } from '../../../utils/getNextActiveField';

const Page = lazy(() => import('../../tool/doc/Page'));

export interface ReviewPageProps {
  pageUrl: string;
  page: {
    pageWidth: number;
    pageHeight: number;
    pageBoundingWidth: number;
    pageBoundingHeight: number;
    pageTokens: Array<IToken>;
    pagePhrases: Array<IToken>;
    pageSubPhrases: Array<Array<IToken>>;
  };
  markedTokens: IToken[];
  extraction: IExtraction;
  fields: string[] | IFieldReview[];
}

const ReviewPage: React.FC<ReviewPageProps> = ({
  pageUrl,
  page,
  markedTokens,
  extraction,
  fields,
}) => {
  const { setToast, showReviewSpreadsheet } = React.useContext(AppContext);
  const { tokens, activeField, fieldListFocused } = React.useContext(MarkStateContext);
  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);

  const { setFeedback } = React.useContext(AppContext);

  const cache = useQueryClient();

  const mutationCreateCorrection = useCreateCorrection(true, true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Correction Saved',
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['extraction']
      });
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToSaveCorrection,
      });
    },
  });

  useEffect(() => {
    if (!showReviewSpreadsheet) {
      dispatch({
        type: 'SET_VISIBLE_EXTRACTIONS',
        payload: [extraction.id],
      });
    }
  }, [extraction, showReviewSpreadsheet]);

  // const [createCorrection] = useMutation(CREATE_CORRECTION);
  // const [updateCorrection] = useMutation(UPDATE_CORRECTION);

  // remove "https://api-sandbox.glynt.ai/v6/"
  const url = pageUrl?.substring(AUTH_CONFIG.apiUrl!.length);
  const { data, isLoading, isError } = usePage(url);

  if (isLoading) {
    return <LinearProgress />;
  }
  if (isError) {
    let message = 'Sorry, failed to load document pages.';
    setFeedback({
      open: true,
      message,
    });
    return <ErrorMsg error={message} />;
  }
  const imageUrl = data?.file_temp_url || '';

  const saveCorrection = async () => {
    if (tokens.length > 0 && activeField) {
      let myTokens = [...tokens];
      const content = myTokens
        .sort((a: IToken, b: IToken) => a.bbox[0].x - b.bbox[0].x)
        .map((t: IToken) => t.content)
        .join(' ');

      const fieldIndex = fields.findIndex((field: string | IFieldReview) => {
        if (typeof field === 'object') {
          return field.label === activeField;
        } else if (typeof field === 'string') {
          return field === activeField;
        }
        return false;
      });

      if (fieldIndex !== -1) {
        let field = fields[fieldIndex];
        if (typeof field !== 'string') {
          field = field.label;
        }

        const correction = { content, tokens };
        await mutationCreateCorrection.mutate({
          extraction: extraction.id,
          field,
          correction,
        });
      } else {
        setToast({
          variant: 'error',
          open: true,
          message: 'Could not find field index.',
        });
        return false;
      }
    }
  };
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.which === 13) {
      await saveCorrection();
      if (activeField) {
        const nextField = getNextActiveField(fields, activeField);
        dispatch({
          type: 'SAVE_FIELD',
          payload: {
            fieldsCount: fields.length,
            ...(nextField && { nextActiveField: nextField }),
            fieldListFocused: fieldListFocused,
          },
        });
      }
    }
  };
  return (
    <Suspense fallback={<LinearProgress />}>
      <Page
        imageUrl={imageUrl}
        page={page}
        markedTokens={markedTokens}
        handleKeyDown={handleKeyDown}
        isReview
      />
    </Suspense>
  );
};

export default ReviewPage;
